import { useEffect, useMemo, useRef, useState } from "react";

import type { DesktopOpenLocalRequest } from "@cmmd-forge/contracts";
import type { OpenLocalMatch } from "@cmmd-forge/shared/openLocalTarget";

import { useProjectsAcrossEnvironments } from "~/hooks/useProjectsAcrossEnvironments";
import { readEnvironmentApi } from "~/environmentApi";
import { resolvePendingHandoff, takeOpenLocalRequest } from "~/openLocalHandoff";
import { performOpenLocalCheckout } from "~/openLocalCheckout";
import { toastManager } from "../ui/toast";
import { OpenLocalHandoffDialog } from "./OpenLocalHandoffDialog";

/**
 * Delivers the `Open locally` handoff: takes the deep-link request the desktop
 * main process is holding, resolves it against local projects, and switches the
 * chosen checkout to the branch.
 *
 * Mounted in the root route alongside the other coordinators.
 *
 * ## Taking and resolving are separate on purpose
 *
 * Taking CLEARS the request in the main process, so it happens exactly once —
 * guarded by a ref rather than an effect dependency, because a re-run would
 * silently consume nothing and look like "no link was clicked".
 *
 * Resolution then re-runs as the project list settles. The list is derived from
 * the store and is empty both while loading and when the user genuinely has no
 * projects, so resolving at mount would answer "no local copy" before the
 * answer is knowable — with the request already gone.
 */
export function OpenLocalHandoffCoordinator() {
  const projects = useProjectsAcrossEnvironments();
  const [request, setRequest] = useState<DesktopOpenLocalRequest | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const takenRef = useRef(false);

  useEffect(() => {
    if (takenRef.current) return;
    takenRef.current = true;
    void takeOpenLocalRequest(globalThis.window?.desktopBridge, {
      onError: (error) => {
        // A failed deep link must not stop the app; it must not be silent either.
        console.error("[open-local] could not read the pending request", error);
      },
    }).then(setRequest);
  }, []);

  const outcome = useMemo(
    () => (dismissed ? null : resolvePendingHandoff(request, projects)),
    [dismissed, request, projects],
  );

  const handleOpen = (match: OpenLocalMatch) => {
    const project = projects.find((candidate) => String(candidate.id) === match.projectId);
    const api = project ? readEnvironmentApi(project.environmentId) : undefined;
    setDismissed(true);

    if (!api || !request) {
      toastManager.add({ title: "Could not reach that project", type: "error" });
      return;
    }

    void performOpenLocalCheckout(
      { refreshStatus: api.git.refreshStatus, checkout: api.git.checkout },
      { cwd: match.workspaceRoot, branch: request.branch },
    ).then((result) => {
      if (!result.ok) {
        toastManager.add({
          title: "Could not switch branch",
          description: result.message,
          type: "error",
        });
        return;
      }
      toastManager.add({
        title: result.changed ? `Switched to ${request.branch}` : `Already on ${request.branch}`,
        type: "success",
      });
    });
  };

  return (
    <OpenLocalHandoffDialog
      outcome={outcome}
      onDismiss={() => setDismissed(true)}
      onOpen={handleOpen}
    />
  );
}
